import h from 'hyperscript';

import editIcon from '@disy/cadenza-icons/edit.svg';
import saveIcon from '@disy/cadenza-icons/save.svg';
import { BackendAbortController } from '@disy/lib.backend/backend-abort-controller';
import { on } from '@disy/lib/event-util';
import { logger } from '@disy/lib/logging';
import { uniqueId } from '@disy/lib/unique-id';
import { createButton } from '@disy/ui/button';
import { icon } from '@disy/ui/icon';
import type { ToastMessage } from '@disy/ui/toast-message';
import { showToastMessage } from '@disy/ui/toast-message';

import { Modal } from 'ui/dialog/modal/modal';

import { confirm, errorAlert } from 'cadenza/alert';
import type { DeletionDeadlineInfoDto } from 'cadenza/api-client/dto/deletion-deadline-dto';
import type { WorkbookVersionDto } from 'cadenza/api-client/dto/workbook-version-dto';
import type { WorkOrderProgressEvent } from 'cadenza/api-client/dto/workorder-dto';
import { getRepositoryNavigatorTree } from 'cadenza/api-client/navigator-tree-api';
import type { DisplayState, SaveWorkbookDto } from 'cadenza/api-client/workbook-api';
import { isWorkbookPublishable, publishWorkbookVersion, saveWorkbookVersion } from 'cadenza/api-client/workbook-api';
import type { SaveWorkbookResult } from 'cadenza/api-client/workbook-api-util';
import type { WorkbookVersionData } from 'cadenza/api-client/workbook-management-api';
import { getWorkbookVersionDto } from 'cadenza/api-client/workbook-management-api';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import { deleteWorkOrders, observeForResult } from 'cadenza/api-client/workorder-api';
import { LoadingMessageDialog } from 'cadenza/loading-message-dialog/loading-message-dialog';
import { reloadNavigatorTreeService } from 'cadenza/navigator/navigator-tree-service/navigator-tree-service';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';
import type { ProjectId } from 'cadenza/repository/model/project-id.gen';
import type { PublicationStage } from 'cadenza/repository/model/publication-stage.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { RepositoryTreeNodeDto } from 'cadenza/repository/model/repository-tree-node-dto';
import { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import type { SaveAsActionParameters, SaveAsOptions } from 'cadenza/save-as';
import { saveAsWorkbook } from 'cadenza/save-as';
import { getStore } from 'cadenza/store/cadenza-store';
import { getProjectId } from 'cadenza/store/cadenza.state';
import { createAndConnectLoadingDialog } from 'cadenza/task-list-dialog/create-task-list-dialog';
import type { TaskListDialogResultType } from 'cadenza/task-list-dialog/task-list-dialog';
import { runTestsuiteForWorkbook } from 'cadenza/testsuite/api/testsuite-api';
import type { ValidationResultEvent } from 'cadenza/testsuite/types';
import { openVersionDialog } from 'cadenza/workbook-version/edit-version-dialog';
import { createSaveAndPublishDialog } from 'cadenza/workbook/header/save-and-publish-workbook-dialog';
import { SaveWorkbookProgressModal } from 'cadenza/workbook/header/save-workbook-actions/save-workbook-progress-modal';
import type { WorkbookVersions } from 'cadenza/workbook/manage-workbook-dialog/workbook-versions/workbook-versions';
import { isPanelVisible } from 'cadenza/workbook/state/workbook-panel.state';
import { saveMapViewsExtentSilently } from 'cadenza/workbook/workbook-core-actions.state';
import { fetchInitialState } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getWorkbookNameAndHash,
  getWorkbookPrintName,
  getWorkbookRepositoryName,
  getWorkbookVersion,
  hasWorkbookInvalidStatus,
  isWorkbookPublished,
  isWorkbookSaved,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { updateObjecttypeBoundStateOnSave, updateWorkbookMetadata } from 'cadenza/workbook/workbook.state';

import './save-workbook-actions.css';
import i18n from './save-workbook-actions.properties';

export interface SaveAndPublishOptions {
  saveWorkbookResult: SaveWorkbookResult;
  savingAndPublishingProcess: boolean;
}

export interface JustPublishOptions {
  savingAndPublishingProcess: boolean;
  version: WorkbookVersionDto;
  workbookVersions: WorkbookVersions;
}

export type PublishOptions = SaveAndPublishOptions | JustPublishOptions;

/**
 * Creates options object for loading dialog for "is workbook publishable?" check
 *
 * @return options object
 */
export function createIsWorkbookPublishableLoadingOptions(
  workbookName: WorkbookName,
  repositoryName: RepositoryName,
  version: number,
) {
  return {
    promise: isWorkbookPublishable(workbookName, repositoryName, version) as Promise<boolean>,
    title: i18n('confirm.no-rights-to-publish-ots.loading-message'),
    processResult: (isPublishable: boolean) => {
      const resultType: TaskListDialogResultType = isPublishable ? 'success' : 'warning';
      return {
        type: resultType,
        message: i18n(`save-and-publish-workbook.{{${resultType}}}.linkedObjecttypes`),
      };
    },
  };
}

/**
 * Creates options object for loading dialog for saving workbook
 *
 * @return options object
 */
function createSavingWorkbookLoadingOptions(
  repositoryName: RepositoryName,
  workbookNameAndHash: WorkbookNameAndHash,
  versionComment: string,
  displayState: DisplayState,
  printName: string,
  folderItemName: string,
  labels: string[],
  projectId?: ProjectId,
  dataProtectionContextId?: DataProtectionContextId,
  deletionDeadlineInfo?: DeletionDeadlineInfoDto,
) {
  const saveWorkbookTaskId = uniqueId();
  return {
    id: saveWorkbookTaskId,
    promise: saveNewWorkbook(
      repositoryName,
      workbookNameAndHash,
      versionComment,
      displayState,
      printName,
      folderItemName,
      labels,
      projectId,
      dataProtectionContextId,
      deletionDeadlineInfo,
    ),
    title: i18n('save-workbook.progress'),
    processResult: () => ({
      type: 'success' as TaskListDialogResultType,
      message: i18n('save-and-publish-workbook.success.saved.loadingSuccessMessage'),
    }),
  };
}

/**
 * Executes "Save As" action, fired from the "Save as" button on the workbook header (visible only when a workbook hasn't been saved yet).
 *
 * @param parameters
 */
async function executeSaveAs(parameters: SaveAsActionParameters) {
  const loadingMessageDialog = createAndConnectLoadingDialog();
  const {
    versionComment,
    name,
    folderItem,
    labels,
    publish,
    projectId,
    dataProtectionContextId,
    deletionDeadlineInfo,
  } = parameters;
  const repositoryName = parameters.repositoryName;
  await saveMapViewsExtentSilently();

  const workbookNameAndHashBeforeSave = getWorkbookNameAndHash(getStore().getState());
  const displayState = getDisplayState();

  try {
    // SAVING WORKBOOK

    // There's always a versionComment (if it's empty, it's an empty string)
    let saveWorkbookResult = await loadingMessageDialog.addTask(
      createSavingWorkbookLoadingOptions(
        repositoryName,
        workbookNameAndHashBeforeSave,
        versionComment!,
        displayState,
        name,
        folderItem?.itemName ?? '',
        labels,
        projectId,
        dataProtectionContextId,
        deletionDeadlineInfo,
      ),
    );

    if (publish) {
      const newWorkbookName = saveWorkbookResult.workbookNameAndHash.id;
      try {
        // CHECKING OT NETWORKS (VISIBILITY AND EDIT-ABILITY)

        const isPublishable = await loadingMessageDialog.addTask(
          createIsWorkbookPublishableLoadingOptions(newWorkbookName, repositoryName, saveWorkbookResult.version),
        );

        if (!isPublishable && !(await createRiskyPublishDialog(true))) {
          loadingMessageDialog.close();
          return Promise.resolve();
        }

        // PUBLISH

        loadingMessageDialog.showCancelButton(i18n('testing.skipTests'));
        loadingMessageDialog.classList.add('visible-footer');

        const workbookId = WorkbookId.of(repositoryName, newWorkbookName);
        const validationResult = await loadingMessageDialog.addTask({
          promise: runWorkbookTestsOrPublishOnSkip(workbookId, loadingMessageDialog, {
            saveWorkbookResult,
            savingAndPublishingProcess: true,
          }),
          title: i18n('testing.loadingResults'),
        });

        if (validationResult) {
          await showWarningOrPublishWorkbook(validationResult, workbookId, loadingMessageDialog, {
            saveWorkbookResult,
            savingAndPublishingProcess: true,
          });
        }
      } catch (error) {
        // if loading dialog is closed on finally, it is still seen for a second after closing the error alert dialog, so we need to close it before
        loadingMessageDialog.close();
        await showPublishWorkbookErrorAlert(error);
        return Promise.reject();
      }
    } else {
      loadingMessageDialog.close();
    }

    return Promise.resolve(saveWorkbookResult); // Need to return the value to update workbook metadata right
  } catch (error) {
    // if loading dialog is closed on finally, it is still seen for a second after closing the error alert dialog, so we need to close it before
    loadingMessageDialog.close();
    await showSaveWorkbookErrorAlert(error);
    return Promise.reject();
  }
}

async function saveNewWorkbook(
  repositoryName: RepositoryName,
  workbookNameAndHash: WorkbookNameAndHash,
  versionComment: string,
  displayState: DisplayState,
  printName: string,
  folderItemName: string,
  labels: string[],
  projectId?: ProjectId,
  dataProtectionContextId?: DataProtectionContextId,
  deletionDeadlineInfo?: DeletionDeadlineInfoDto,
) {
  const saveWorkbookProgressModal = new SaveWorkbookProgressModal();
  const modalClosed = saveWorkbookProgressModal.connectAndShow({ hidePreviousModals: false, closeAll: false });
  try {
    const options: SaveWorkbookDto = {
      comment: versionComment,
      displayState,
      printName,
      folderItemName,
      labels,
      dataProtectionContextId,
      workbookNameAndHash,
      deletionDeadlineInfo,
    };
    if (projectId) {
      options.projectName = projectId.itemName; // Pass projectName if present
    }
    const saveWorkbookResult = await saveWorkbookProgressModal.saveWorkbookWithProgress(repositoryName, options);
    await modalClosed;
    await updateStateAfterSavingWorkbook(saveWorkbookResult, 'DEVELOPMENT');
    getStore().dispatch(updateObjecttypeBoundStateOnSave());
    showWorkbookSavedToast();
    return saveWorkbookResult;
  } catch (error) {
    saveWorkbookProgressModal.close();
    throw error;
  }
}

/**
 * Saves a new workbook version, updates the state with the new information and shows a toast message showing the process has been successful.
 */
async function saveAndUpdateWorkbook(
  versionComment: string,
  repositoryName: RepositoryName,
  workbookNameAndHash: WorkbookNameAndHash,
) {
  const saveWorkbookResult = await saveWorkbookVersion(repositoryName, workbookNameAndHash, {
    comment: versionComment,
    displayState: getDisplayState(),
  });

  getStore().dispatch(updateObjecttypeBoundStateOnSave());
  showWorkbookSavedToast();

  await updateStateAfterSavingWorkbook(saveWorkbookResult, 'DEVELOPMENT');

  return saveWorkbookResult;
}

/**
 * Publishes workbook version, updates state and shows toast message; or shows error dialog.
 *
 * @param repositoryName
 * @param publishOptions
 */
async function publishWorkbookAfterSaving(repositoryName: RepositoryName, publishOptions: SaveAndPublishOptions) {
  const saveWorkbookResult = publishOptions.saveWorkbookResult;
  try {
    if (saveWorkbookResult) {
      await publishWorkbookVersion(
        saveWorkbookResult.workbookNameAndHash.id,
        repositoryName,
        saveWorkbookResult.version,
      );
      await updateStateAfterSavingWorkbook(saveWorkbookResult, 'PUBLISHED');
      showWorkbookPublishedToast();
    }
  } catch (error) {
    await showPublishWorkbookErrorAlert(error);
  }
}

/**
 * Publishes workbook version, rerenders versions and updates metadata.
 *
 * @param publishOptions
 */
async function publishWorkbook(publishOptions: JustPublishOptions) {
  try {
    const workbookVersions = publishOptions.workbookVersions;
    const version = publishOptions.version;
    await publishWorkbookVersion(workbookVersions.workbookName, workbookVersions.repositoryName, version.version!);
    renderVersionsAndUpdateMetadata(workbookVersions, version);
    showWorkbookPublishedToast();
  } catch (error) {
    await showPublishWorkbookErrorAlert(error);
  }
}

/**
 * Runs testsuite for workbook and publishes workbook or shows warning dialog, and saves validation result on sessionStorage.
 */
export function runWorkbookTestsOrPublishOnSkip(
  workbookId: WorkbookId,
  loadingMessageDialog: Modal,
  publishOptions: PublishOptions,
) {
  const abortController = new BackendAbortController();
  let currentWorkOrderId: WorkOrderId | null;

  loadingMessageDialog.addEventListener('cancel', () => {
    deleteWorkOrders([currentWorkOrderId!]);
    currentWorkOrderId = null;
    if (publishOptions.savingAndPublishingProcess) {
      // save + publish process
      publishWorkbookAfterSaving(workbookId.repositoryName, publishOptions as SaveAndPublishOptions);
    } else {
      // just publish process
      publishWorkbook(publishOptions as JustPublishOptions);
    }
  });

  // 'DEVELOPMENT' mode on purpose as that will use the workbook version and the objecttypes that are about to be published, which is exactly what we want to validate
  return runTestsuiteForWorkbook(workbookId, 'DEVELOPMENT', abortController.signal)
    .then((workOrderId) => {
      currentWorkOrderId = workOrderId;
      /* delete workorder on leaving the page */
      window.addEventListener('beforeunload', () => deleteWorkOrders([currentWorkOrderId!]));
      return observeForResult<unknown, WorkOrderProgressEvent, ValidationResultEvent>(
        currentWorkOrderId,
        'workbook-validation-result',
      );
    })
    .then((validationResult) => {
      if (!currentWorkOrderId) {
        // avoids continuing testing after user skips it on slow internet connections
        return;
      }
      return validationResult;
    })
    .catch((error) => {
      logger.error(error);
      deleteWorkOrders([currentWorkOrderId!]);
    });
}

export async function createRiskyPublishDialog(showSaveSuccessInfoMessage?: boolean) {
  return new Promise((resolve) => {
    const dialog = new Modal({
      cancelButton: i18n('testing.cancel.title'),
      submitButton: i18n('testing.continue.title'),
      title: 'WARNING',
      titleIcon: icon(saveIcon),
      content: () =>
        Promise.resolve(
          h('div', [
            h('h1', i18n('confirm.no-rights-to-publish-ots.title')),
            h('p', i18n('confirm.no-rights-to-publish-ots.message.firstPart')),
            h('p', i18n('confirm.no-rights-to-publish-ots.message.secondPart')),
            showSaveSuccessInfoMessage ?
              h(
                '.d-alert.d-alert-success',
                h('strong', i18n('save-and-publish-workbook.success.saved.infoMessageAfterTesting')),
              )
            : '',
          ]),
        ),
    });
    dialog.classList.add('d-publishable-workbook-warning-dialog');

    on(dialog, 'cancel', () => {
      resolve(false);
    });

    on(dialog, 'submit', () => {
      dialog.close();
      resolve(true);
    });

    dialog.connectAndShow();
  });
}

/**
 * Saves and publishes a workbook.
 *
 * @param versionComment
 */
async function saveAndPublishWorkbook(versionComment: string) {
  // SAVING WORKBOOK

  const loadingMessageDialog = createAndConnectLoadingDialog();

  await saveMapViewsExtentSilently();

  try {
    const workbookNameAndHashBeforeSave = getWorkbookNameAndHash(getStore().getState());
    const repositoryName = getWorkbookRepositoryName(getStore().getState());

    const saveWorkbookTaskId = uniqueId();
    const saveWorkbookResult = await loadingMessageDialog.addTask({
      id: saveWorkbookTaskId,
      promise: saveAndUpdateWorkbook(versionComment, repositoryName, workbookNameAndHashBeforeSave),
      title: i18n('save-workbook.progress'),
      processResult: () => ({
        type: 'success',
        message: i18n('save-and-publish-workbook.success.saved.loadingSuccessMessage'),
      }),
    });

    try {
      // CHECKING OT NETWORKS (VISIBILITY AND EDITABILITY)

      const newWorkbookName = saveWorkbookResult.workbookNameAndHash.id;
      const isPublishable = await loadingMessageDialog.addTask(
        createIsWorkbookPublishableLoadingOptions(
          newWorkbookName,
          repositoryName,
          getWorkbookVersion(getStore().getState())!,
        ),
      );

      if (!isPublishable && !(await createRiskyPublishDialog(true))) {
        loadingMessageDialog.close();
        return;
      }

      loadingMessageDialog.showCancelButton(i18n('testing.skipTests'));
      loadingMessageDialog.classList.add('visible-footer');

      const workbookId = WorkbookId.of(repositoryName, newWorkbookName);
      const validationResult = await loadingMessageDialog.addTask({
        promise: runWorkbookTestsOrPublishOnSkip(workbookId, loadingMessageDialog, {
          saveWorkbookResult,
          savingAndPublishingProcess: true,
        }),
        title: i18n('testing.loadingResults'),
      });

      if (validationResult) {
        showWarningOrPublishWorkbook(validationResult, workbookId, loadingMessageDialog, {
          saveWorkbookResult,
          savingAndPublishingProcess: true,
        });
      }
    } catch (error) {
      // if loading dialog is closed on finally, it is still seen for a second after closing the error alert dialog, so we need to close it before
      loadingMessageDialog.close();
      await showPublishWorkbookErrorAlert(error);
    }
  } catch (error) {
    // if loading dialog is closed on finally, it is still seen for a second after closing the error alert dialog, so we need to close it before
    loadingMessageDialog.close();
    await showSaveWorkbookErrorAlert(error);
  }
}

/**
 * Checks validation result and shows warning dialog if there are errors/warnings or publishes workbook if not.
 *
 * @return A Promise for when the workbook is processed
 */
export async function showWarningOrPublishWorkbook(
  validationResult: ValidationResultEvent,
  workbookId: WorkbookId,
  loadingMessageDialog: Modal,
  publishOptions: PublishOptions,
) {
  // There's only one entry: the one for this workbook
  const entry = validationResult.results.entries[0];
  let numberOfWarnings = 0;
  let numberOfErrors = 0;

  for (const entity of entry.entities) {
    numberOfWarnings += entity.warnings;
    numberOfErrors += entity.failures;
  }
  if (numberOfWarnings > 0 || numberOfErrors > 0) {
    const testSuiteResultId = validationResult.workOrderId;

    // Show the warning dialog
    const { PublishWorkbookValidationDialog } =
      await import('cadenza/workbook/header/save-workbook-actions/publish-workbook-validation-dialog');
    const validationDialog = new PublishWorkbookValidationDialog({
      numberOfErrors,
      numberOfWarnings,
      loadingMessageDialog,
      workbookId,
      testSuiteResultId,
      savingAndPublishingProcess: publishOptions.savingAndPublishingProcess,
      publish: () =>
        publishOptions.savingAndPublishingProcess ?
          publishWorkbookAfterSaving(workbookId.repositoryName, publishOptions as SaveAndPublishOptions)
        : publishWorkbook(publishOptions as JustPublishOptions),
    });
    loadingMessageDialog.classList.remove('visible-footer');

    on(window, 'beforeunload', () => deleteWorkOrders([testSuiteResultId]));
    on(validationDialog, 'close', () => deleteWorkOrders([testSuiteResultId]));

    return validationDialog.connectAndShow();
  } else {
    loadingMessageDialog.closeAll();
    return publishOptions.savingAndPublishingProcess ?
        publishWorkbookAfterSaving(workbookId.repositoryName, publishOptions as SaveAndPublishOptions)
      : publishWorkbook(publishOptions as JustPublishOptions);
  }
}

/**
 * Shows dialog to add new version comment and confirm save and publish.
 */
async function executeSaveAndPublishWorkbook() {
  const versionComment = await createSaveAndPublishDialog();
  if (typeof versionComment === 'string') {
    saveAndPublishWorkbook(versionComment);
  }
}

export async function executeSaveWorkbook() {
  const loadingMessageDialog = new LoadingMessageDialog();
  loadingMessageDialog.setLoadingMessage(i18n('save-workbook.progress'));
  loadingMessageDialog.connectAndShow();

  try {
    await saveMapViewsExtentSilently();
    const saveWorkbookResult = await saveWorkbookVersion(
      getWorkbookRepositoryName(getStore().getState()),
      getWorkbookNameAndHash(getStore().getState()),
      { displayState: getDisplayState() },
    );
    await updateStateAfterSavingWorkbook(saveWorkbookResult, 'DEVELOPMENT');
    getStore().dispatch(updateObjecttypeBoundStateOnSave());
    loadingMessageDialog.close();
    showSavedVersionToastMessage(saveWorkbookResult);
  } catch (error) {
    loadingMessageDialog.close();
    showSaveWorkbookErrorAlert(error);
  }
}

async function updateStateAfterSavingWorkbook(
  saveWorkbookResult: SaveWorkbookResult,
  publicationStage: PublicationStage,
) {
  const { workbookNameAndHash, version, printName } = saveWorkbookResult;
  const previousWorkbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
  getStore().dispatch(
    updateWorkbookMetadata({
      printName,
      workbookNameAndHash,
      saved: true,
      changed: 'no',
      clientChanged: false,
      version,
      publicationStage,
    }),
  );
  await Promise.all([
    previousWorkbookNameAndHash.id !== workbookNameAndHash.id && getStore().dispatch(fetchInitialState()).unwrap(),
    reloadNavigatorTreeService(),
  ]);
}

function getDisplayState() {
  return {
    isAnalysisContextDisplayed: isPanelVisible(getStore().getState(), WorkbookPanelName.ANALYSIS_CONTEXT),
    isDesignerDisplayed: isPanelVisible(getStore().getState(), WorkbookPanelName.VIEW_SETTINGS),
    isDataBrowserDisplayed: isPanelVisible(getStore().getState(), WorkbookPanelName.DATA_BROWSER),
  };
}

function showWorkbookSavedToast() {
  return showToastMessage(i18n('save-workbook.finished'), { type: 'success' });
}

function showWorkbookPublishedToast() {
  return showToastMessage(i18n('save-and-publish-workbook.published.finished'), { type: 'success' });
}

function showSaveWorkbookErrorAlert(err: unknown) {
  logger.error('Error when trying to save workbook ', err);
  return errorAlert(i18n('save-workbook.error'));
}

function showPublishWorkbookErrorAlert(err: unknown) {
  logger.error('Error when trying to publish workbook ', err);
  return errorAlert(i18n('error.publish.title'), i18n('error.publish.message'));
}

function showSavedVersionToastMessage(saveWorkbookResult: SaveWorkbookResult) {
  const { workbookNameAndHash, repositoryName, version } = saveWorkbookResult;
  const workbookName = workbookNameAndHash.id;
  let message: ToastMessage;
  const content = h('.d-stack-v.space-2', [
    h('span', i18n('save-workbook.version-saved', { version })),
    createButton([icon(editIcon), i18n('save-workbook.add-version-comment-button')], {
      size: 'm',
      styleClass: 'toast-edit-version-comment',
      onclick: () => {
        message.remove();
        getWorkbookVersionDto(workbookName, repositoryName, version)
          .then((versionData) => openVersionDialog(workbookName, repositoryName, versionData))
          .catch((error) =>
            logger.error(`Failed to load workbook's version: ${workbookName}/${repositoryName} v${version}`, error),
          );
      },
    }),
  ]);
  message = showToastMessage(content, { type: 'success' });
}

export async function saveAndPublishWorkbookAction() {
  if (hasWorkbookInvalidStatus() && !(await confirmSaveInvalidWorkbook())) {
    return;
  }
  return executeSaveAndPublishWorkbook();
}

export async function saveWorkbookAction() {
  if (hasWorkbookInvalidStatus() && !(await confirmSaveInvalidWorkbook())) {
    return;
  }
  return executeSaveWorkbook();
}

function confirmSaveInvalidWorkbook() {
  return confirm(i18n('workbook-in-invalid-status.title'), i18n('workbook-in-invalid-status.message'), {
    type: 'warning',
    submit: i18n('workbook-in-invalid-status.submit'),
  });
}

export async function executeSaveAsAction(callback?: () => Promise<void>) {
  if (hasWorkbookInvalidStatus() && !(await confirmSaveInvalidWorkbook())) {
    return;
  }

  const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
  const workbookName = getWorkbookPrintName(getStore().getState());

  const defaultRepositoryName = getWorkbookRepositoryName(getStore().getState());
  const repositoryTreeData = await getRepositoryNavigatorTree(defaultRepositoryName);

  let saveAsOptions: SaveAsOptions = {
    defaultName:
      isWorkbookSaved(getStore().getState()) ? `${workbookName} ${i18n('save-workbook.copy-suffix')}` : workbookName,
    defaultRepositoryName,
    nameLabel: i18n('save-workbook.name-label'),
    projectId: getProjectId(getStore().getState()),
    labels: findItem(workbookNameAndHash.id, repositoryTreeData?.children)?.labels ?? [],
  };
  if (callback) {
    saveAsOptions.afterSaveAction = {
      label: i18n('after-save-action-label'),
      execute: callback,
    };
  }

  return saveAsWorkbook(executeSaveAs, saveAsOptions);
}

function findItem(
  workbookName: WorkbookName,
  repositoryItems?: RepositoryTreeNodeDto[],
): RepositoryTreeNodeDto | undefined {
  if (repositoryItems && Array.isArray(repositoryItems) && repositoryItems.length > 0) {
    const found = repositoryItems.find((item) => item.itemName === workbookName);
    return (
      found ||
      findItem(
        workbookName,
        repositoryItems.flatMap((item) => item?.children ?? []).filter((item) => !!item),
      )
    );
  }
}

/**
 * Renders workbook versions and updates metadata 'versionMode' property.
 *
 * @param workbookVersions
 * @param version
 */
function renderVersionsAndUpdateMetadata(workbookVersions: WorkbookVersions, version: WorkbookVersionData) {
  workbookVersions.render();

  if (version.isCurrentState) {
    getStore().dispatch(updateWorkbookMetadata({ publicationStage: 'PUBLISHED' }));
  } else {
    // other version than the loaded one was published → if the loaded version was published before, then it needs to be refreshed
    const loadedVersionNumber = getWorkbookVersion(getStore().getState());
    if (isWorkbookPublished(getStore().getState()) && loadedVersionNumber === workbookVersions.maxVersion) {
      // if the last version was loaded and was published it will be development
      getStore().dispatch(updateWorkbookMetadata({ publicationStage: 'DEVELOPMENT' }));
    }
  }
}
